import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Copy, Plus, Trash2, Power, PowerOff, RefreshCw } from 'lucide-react';

export default function MobileTokenManager() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [createdToken, setCreatedToken] = useState(null);

  useEffect(() => {
    loadTokens();
  }, []);

  const loadTokens = async () => {
    try {
      setLoading(true);
      const response = await api.get('/superadmin/mobile-api-tokens');
      setTokens(response.data);
    } catch (error) {
      toast.error('Failed to load tokens');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async () => {
    if (!newTokenName.trim()) {
      toast.error('Token name is required');
      return;
    }

    try {
      const response = await api.post('/superadmin/mobile-api-tokens', {
        name: newTokenName
      });
      
      setCreatedToken(response.data);
      setNewTokenName('');
      toast.success('Mobile API Token created!');
      loadTokens();
    } catch (error) {
      toast.error('Failed to create token');
      console.error(error);
    }
  };

  const handleToggleStatus = async (tokenId, currentStatus) => {
    try {
      await api.put(`/superadmin/mobile-api-tokens/${tokenId}`, {
        is_active: !currentStatus
      });
      
      toast.success(`Token ${!currentStatus ? 'activated' : 'deactivated'}`);
      loadTokens();
    } catch (error) {
      toast.error('Failed to update token status');
      console.error(error);
    }
  };

  const handleDeleteToken = async (tokenId, tokenName) => {
    if (!window.confirm(`Are you sure you want to delete token "${tokenName}"?`)) {
      return;
    }

    try {
      await api.delete(`/superadmin/mobile-api-tokens/${tokenId}`);
      toast.success('Token deleted');
      loadTokens();
    } catch (error) {
      toast.error('Failed to delete token');
      console.error(error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Token copied to clipboard!');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mobile API Tokens</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage static tokens for Mobile App authentication
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={18} />
          Create Token
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="animate-spin mx-auto mb-2" size={32} />
          <p className="text-gray-600">Loading tokens...</p>
        </div>
      ) : tokens.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <p className="text-gray-600 mb-4">No Mobile API Tokens yet</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create First Token
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Token
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Used
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tokens.map((token) => (
                <tr key={token.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{token.name}</div>
                    <div className="text-xs text-gray-500">by {token.created_by}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                        {token.token.substring(0, 30)}...
                      </code>
                      <button
                        onClick={() => copyToClipboard(token.token)}
                        className="p-1 hover:bg-gray-200 rounded"
                        title="Copy token"
                      >
                        <Copy size={16} className="text-gray-600" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {token.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Power size={12} />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <PowerOff size={12} />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatDate(token.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatDate(token.last_used_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleToggleStatus(token.id, token.is_active)}
                      className={`mr-3 ${
                        token.is_active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'
                      }`}
                      title={token.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {token.is_active ? <PowerOff size={18} /> : <Power size={18} />}
                    </button>
                    <button
                      onClick={() => handleDeleteToken(token.id, token.name)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete token"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Token Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Create Mobile API Token</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Token Name
              </label>
              <input
                type="text"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
                placeholder="e.g., iOS Production App"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                Give a descriptive name to identify this token
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewTokenName('');
                  setCreatedToken(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateToken}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Token
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Show Created Token Modal */}
      {createdToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <h2 className="text-xl font-bold mb-4 text-green-600">✅ Token Created Successfully!</h2>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-yellow-800 mb-2">
                ⚠️ Important: Copy this token now!
              </p>
              <p className="text-xs text-yellow-700">
                You won't be able to see the full token again. Share it securely with your mobile developers.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Token Name
              </label>
              <div className="text-lg font-semibold">{createdToken.name}</div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Token
              </label>
              <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                <code className="flex-1 text-sm font-mono break-all">
                  {createdToken.token}
                </code>
                <button
                  onClick={() => copyToClipboard(createdToken.token)}
                  className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                >
                  <Copy size={16} />
                  Copy
                </button>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-blue-800 mb-2">
                📱 Usage in Mobile App
              </p>
              <code className="text-xs text-blue-700 block">
                Header: X-Mobile-Token: {createdToken.token.substring(0, 30)}...
              </code>
            </div>

            <button
              onClick={() => {
                setCreatedToken(null);
                setShowCreateModal(false);
              }}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
